/* eslint-disable indent */
import { html, css, LitElement } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import {
    loadDraftsIfNeeded,
    publishDraft,
    unpublishDraft,
    createDraft,
    duplicateDraft,
    deleteDraft,
} from '../../store/draft/draft.js';
import { navigate } from '../../store/route/route.js';
import { previewDraft } from '../../store/preview/player.js';
import { translate } from '../../helpers/translate.js';
import { renderTimestampField } from '../../helpers/controls.js';
import '@lit-labs/virtualizer';
import '@polymer/app-layout/app-layout.js';
import '@vaadin/context-menu/theme/material/vaadin-context-menu.js';
import '@material/web/fab/fab.js';
import '@sdb-develop/learning-frontend';
import '../shared/sdb-search-input.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-status-message.js';
import '../shared/sdb-dialog.js';
import { DisabledTransportError } from '@microsoft/signalr/dist/esm/Errors.js';

@customElement('sdb-composition-draft-list')
class SdbCompositionDraftList extends connect(store)(LitElement) {

    @state()
    accessor drafts;

    @state()
    accessor total;

    @state()
    accessor search;

    @state()
    accessor isLoadingFirstTime;

    @state()
    accessor user;

    get newTitle() {
        return document.getElementById('composition_new_title');
    }

    stateChanged(state) {
        this.drafts = state.draft.list;
        this.total = state.draft.total;
        this.search = state.draft.search;
        this.isLoadingFirstTime = state.draft.status === 'loading' && state.draft.page === -1;
        this.user = state.auth.user;
        this.hasGlobalId = state.env.hasGlobalId;
    };

    static styles = [
        sharedStyles,
        css`
            :host {
                max-width: var(--tcg-content-max-width);
                width: 100%;
            }

            #drafts_list {
                contain: none !important; /* lit-virtualizer hack */
            }

            sdb-content-listitem {
                width: 100%; /* lit-virtualizer screws this up */
            }

            .no-results {
                width: 100%;
                max-width: var(--tcg-content-max-width);
                height: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            #no_results_img {
                width: 400px;
                height: 400px;
                background-color: var(--tcg-secondary-background-color);
            }
        `,
    ];

    render() {
        if (this.isLoadingFirstTime) {
            return html`<sdb-loading></sdb-loading>`;
        }

        return html`
            <sdb-layout-60-40>
                <sdb-nav-titlebar slot="left">
                    <span>${translate('headerDrafts')}</span>
                    <span ?hidden=${this.total === undefined}>&nbsp;(${this.total})</span>
                </sdb-nav-titlebar>
                <sdb-search-input
                    slot="right"
                    placeholder=${translate('search')}
                    value=${this.search}
                    @search=${(e) => { store.dispatch(loadDraftsIfNeeded({ search: e.detail.searchText, reload: true })); }}>
                </sdb-search-input>
            </sdb-layout-60-40>
            <sdb-layout-100>
                ${!this.total
                    ? html`<sdb-status-message label=${translate('noCompositionsFound')}></sdb-status-message>`
                    : html`
                        <sdb-content-list separator>
                            <lit-virtualizer
                                id="drafts_list"
                                .items=${this.drafts}
                                .renderItem=${(draft, index) => this._renderDraftListItem(draft, index)}
                                .scrollTarget=${document}>
                            </lit-virtualizer>
                        </sdb-content-list>
                    `
                }
                <md-fab @click=${()=> { this._openDialog('add', null); }}>
                    <sdb-content-icon
                        slot="icon"
                        icon="fal fa-plus"
                        size="24">
                    </sdb-content-icon>
                </md-fab>
            </sdb-layout-100>

            <sdb-dialog
                type="confirm"
                id="publish_dialog"
                heading=${translate('publishHeading')}
                body=${translate('publishBody')}
                confirmCaption=${translate('publish')}
                @confirm=${(e) => { store.dispatch(publishDraft({ draftId: e.detail.args })); }}>
            </sdb-dialog>

            <sdb-dialog
                type="confirm"
                id="unpublish_dialog"
                heading=${translate('unpublishHeading')}
                body=${translate('unpublishBody')}
                confirmCaption=${translate('unpublish')}
                @confirm=${(e) => { store.dispatch(unpublishDraft({ uniqueId: e.detail.args })); }}>
            </sdb-dialog>

            <sdb-dialog
                type="confirm"
                id="duplicate_dialog"
                heading=${translate('duplicateHeading')}
                confirmCaption=${translate('duplicate')}
                @confirm=${(e) => { store.dispatch(duplicateDraft({ draftId: e.detail.args })); }}>
            </sdb-dialog>

            <sdb-dialog
                type="confirm"
                id="delete_dialog"
                heading=${translate('deleteHeading')}
                confirmCaption=${translate('delete')}
                @confirm=${(e) => { store.dispatch(deleteDraft({ draftId: e.detail.args })); }}>
            </sdb-dialog>

            <sdb-dialog
                type="form"
                id="add_dialog"
                heading=${translate('createHeading')}
                confirmCaption=${translate('create')}
                @confirm=${() => { store.dispatch(createDraft({ title: this.newTitle.value })); this.newTitle.value = ''; }}
                .bodyRenderer=${() => html`
                    <paper-input
                        id="composition_new_title"
                        transparent
                        style="width: 500px;"
                        label=${translate('title')}>
                    </paper-input>
                `}>
            </sdb-dialog>
        `;
    }

    firstUpdated() {
        this.dispatchEvent(
            new CustomEvent('fullscreen', {
                bubbles: true,
                composed: true,
                detail: {
                    fullscreen: false,
                },
            }),
        );
    }

    _renderDraftListItem(draft, index) {
        store.dispatch(loadDraftsIfNeeded({ index }));

        const menuItems = [
            {
                text: translate('publish'),
                action: () => this._openDialog('publish', draft.id),
            },
            {
                text: translate('unpublish'),
                action: () => this._openDialog('unpublish', draft.uniqueId),
                disabled: !draft.distributed,
                hidden: this.hasGlobalId,
            },
            {
                text: translate('preview'),
                action: () => previewDraft(draft.id),
            },
            {
                text: translate('duplicate'),
                action: () => this._openDialog('duplicate', draft.id),
            },
            { component: 'hr' },
            {
                text: translate('delete'),
                action: () => this._openDialog('delete', draft.id),
                className: 'warning',
                // the API refuses to delete a published composition: unpublish it first
                disabled: draft.distributed,
            },
        ].filter(item => !item.hidden);

        return html`
            <sdb-content-listitem
                icon="fal fa-square-pen"
                .headline=${draft.title}
                .subtitle=${this._getSubtitle(draft)}
                @click=${()=> { store.dispatch(navigate(`draft/${draft.id}`)); }}>

                <sdb-content-chipbar slot="chipbar">
                    <sdb-content-chip label=${translate('catalog')} ?hidden=${!draft.distributed || this.hasGlobalId}></sdb-content-chip>
                </sdb-content-chipbar>

                <vaadin-context-menu
                    slot="secondaryAction"
                    open-on="click"
                    .items=${menuItems}
                    @item-selected=${(e) => {
                        e.stopPropagation();
                        e.detail.value?.action?.();
                    }}>
                    <sdb-content-iconbutton icon="fal fa-ellipsis-vertical"></sdb-content-iconbutton>
                </vaadin-context-menu>

            </sdb-content-listitem>
        `;
    }

    _getSubtitle(draft) {
        let s = '';
        if (draft.lastModified) {
            s += `${translate('lastModified')}: ${renderTimestampField(draft.lastModified)}`;
        }
        if (draft.lastPublished) {
            if (s.length > 0) {
                s += ' ,';
            }
            s += `${translate('lastPublished')}: ${renderTimestampField(draft.lastPublished)}`;
        }
        return s;
    }

    _renderTimestamp(field) {
        return moment((field || {}).timestamp).fromNow();
    }

    _openDialog(action, args) {
        this.renderRoot.querySelector(`#${action}_dialog`).open(args);
    }
}

